// What the `host` categories are CALLED on screen.
//
// A rename lands here rather than in the column, because three separate things
// key off the stored string: filter groups reference hosts by exact string
// (shows' array is literally ['Theater','Live Music','Comedy']), the colour
// palette hashes the same strings, and the ?hosts= slugs in links already pasted
// into public comments are derived from them. A rename that only wanted new
// words on screen would break all three to get them.
//
// Changing the data IS possible - the 7/29 taxonomy did it - but it is a
// migration that carries all three consumers with it, not an edit to this file.
// See supabase/migrations/20260729223000_category_taxonomy.sql.
//
// EVERY surface that shows a category name reads through hostLabel - the drawer
// checkboxes, the search suggestions, the banner's Includes line, the event
// modal, the approval queue. A rename that reached one of those and not the
// others is worse than no rename at all, because the two names would look like
// two different categories.
//
// Two entries came OUT of this map on 7/29, when the #27 audit shipped at the
// data layer. 'Wine & Drinks' -> 'Food & Drink' was here because the column
// could not be renamed on its own; now the column says 'Food & Drink' and a
// display entry would be mapping a value that no longer exists. Same for
// 'Class/Workshop' -> 'Workshops & Courses', a category that was retired
// outright. A map entry for a dead stored value is invisible dead weight: it
// never fires, and it reads to the next person as though the category is still
// live. An entry belongs here only while the column and the screen disagree.
const DISPLAY: Record<string, string> = {
  'Art & Craft': 'Arts & Crafts',
  Market: 'Farmer and Artisan Markets',
  // Open Mic was folded into Live Music in the 7/28 host merge, and three of the
  // 49 rows are open mics. The category absorbed them without saying so, which
  // left someone looking for one with no reason to tick this box.
  'Live Music': 'Live Music & Open Mic',
}

const RAW: Record<string, string> = Object.fromEntries(
  Object.entries(DISPLAY).map(([raw, shown]) => [shown.toLowerCase(), raw]),
)

/** The on-screen name for a stored host value. Unmapped values pass through. */
export function hostLabel(host: string | null | undefined): string {
  if (!host) return ''
  return DISPLAY[host] ?? host
}

/**
 * Back the other way, for the submit form: someone picking "Arts & Crafts" from
 * the list must still store 'Art & Craft', or the public form would quietly
 * invent a sixteenth category that nothing else knows about. Anything typed
 * freehand passes through untouched.
 */
export function rawHost(label: string): string {
  return RAW[label.trim().toLowerCase()] ?? label.trim()
}

/** Both spellings, for matching text someone typed against a category. */
export function hostSearchTerms(host: string | null | undefined): string[] {
  if (!host) return []
  const shown = DISPLAY[host]
  return shown ? [host, shown] : [host]
}
